import moment from 'moment';
import { FC, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import {
  getGenderOptionsForRestriction,
  normaliseGenderCode,
} from '@common/helpers/genderRestriction';
import {
  useBookingFormLabels,
  useCalendarNavigation,
  useGetSpaSettings,
  useGetSpaTreatment,
  useGetSpaTreatmentTimeslots,
  useGuestCountChange,
  useNotesPreferences,
  useSyncSpaDateToRedux,
  useTimeslotAvailability,
} from '@features/spa/hooks';
import { mapSlotsToTimeslotChips } from '@features/spa/utils/mapSlotsToTimeslotChips';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { buildExistingGuests } from '@utils/buildExistingGuests';
import { useExistingGuestSelect } from '@utils/useExistingGuestSelect';
import { useRouter } from 'shared-routing';
import { Property } from 'shared-types/Property';

import type { SpaGuestConfig } from 'voyager';
import { SpaTreatmentFacilityBookingForm } from 'voyager';

import { Guest } from 'shared-types/Basket';
import { useSubmitTreatment } from './hooks/useSubmitTreatment';
import { SpaTreatmentFormValues } from './SpaTreatmentBookingV2.types';
import { spaEntityFormSchema } from './SpaTreatmentForm.schema';

type SpaTreatmentBookingV2Props = {
  id: number;
  property: Property;
  language: string;
  onClose: (showMiniBasket: boolean) => void;
  loggedInGuest?: Guest | null;
};

export const SpaTreatmentBookingV2: FC<SpaTreatmentBookingV2Props> = ({
  id,
  property,
  language,
  onClose,
  loggedInGuest,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams) as any;
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  );
  const basketGuests = useSelector((state: any) => basketSelectors.getBasketGuests(state)) || [];

  const existingGuests = useMemo(
    () => buildExistingGuests(basketGuests, loggedInGuest),
    [basketGuests, loggedInGuest]
  );

  const form = useFormContext<SpaTreatmentFormValues>();

  const { data: spaEntity, isFetching: isLoadingEntity } = useGetSpaTreatment(
    property.id,
    language,
    id,
    moment(journeyParams.spaDate).format('YYYY-MM-DD')
  );
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const showGenderNeutral = spaSettings?.showGenderNeutral || false;

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedDate = useWatch({ control: form.control, name: 'date' });
  const watchedArrivalTime = useWatch({ control: form.control, name: 'arrivalTime' });

  const selectedDateString = (
    watchedDate || moment(journeyParams.spaDate).format('YYYY-MM-DD')
  ).trim();

  useSyncSpaDateToRedux(selectedDateString, journeyParams.spaDate);

  const bookingLabels = useBookingFormLabels(language, {
    phoneNumber: spaSettings?.phoneNumber,
    entityName: spaEntity?.name,
    spaEntityLabel: t('spa.spaTreatment').toLowerCase(),
    pluralEntityLabel: t('app.spaTreatments'),
    formattedDate: moment(selectedDateString).format('dddd Do MMMM YYYY'),
    onCtaClick: () => router.push({ pathname: Routes.SPA_TREATMENT_LIST }),
  });

  const {
    weekStartDate,
    setWeekStartDate,
    calendarView,
    setCalendarView,
    handleCalendarViewChange,
    debouncedWeekStartDate,
  } = useCalendarNavigation({ initialDate: selectedDateString, form });

  const {
    data: rawTimeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
  } = useGetSpaTreatmentTimeslots(
    property.id,
    Number(id),
    debouncedWeekStartDate,
    language,
    undefined,
    calendarView === 'week',
    7
  );

  const minGuests = spaEntity?.minimumGuests || 1;
  const maxGuests = spaEntity?.maximumGuests || minGuests;

  const guestCount =
    watchedGuests?.length ||
    Math.max(minGuests, Math.min(maxGuests, journeyParams?.spaAdults || 1));

  const {
    slotsByDate,
    availability,
    handleDateSelect,
    hasNoAvailabilityAllWeek,
    hasNoAvailabilityToday,
  } = useTimeslotAvailability({
    rawTimeslots,
    selectedDateString,
    weekStartDate,
    debouncedWeekStartDate,
    isLoadingTimeslots,
    isTimeslotsError,
    form,
    setWeekStartDate,
    calendarView,
    setCalendarView,
    guestCount,
  });

  const timeslotChips = useMemo(
    () => mapSlotsToTimeslotChips(slotsByDate, selectedDateString, guestCount),
    [slotsByDate, selectedDateString, guestCount]
  );

  useEffect(() => {
    if (!watchedArrivalTime) return;
    const daySlots = slotsByDate[selectedDateString];
    const matching = daySlots?.find((s) =>
      watchedArrivalTime.startDateTime
        ? moment(s.startDateTime).isSame(moment(watchedArrivalTime.startDateTime), 'minute')
        : moment(s.startDateTime).format('HH:mm') === watchedArrivalTime.startTime
    );
    const stillAvailable =
      !!matching &&
      (typeof matching.numberAvailable !== 'number' || matching.numberAvailable >= guestCount);
    if (!stillAvailable) {
      form.setValue('arrivalTime', undefined, { shouldValidate: true, shouldDirty: true });
    }
  }, [slotsByDate, selectedDateString, guestCount, watchedArrivalTime, form]);

  const defaultGender =
    spaEntity?.genderSpecific === 'M' ? 'male' : spaEntity?.genderSpecific === 'F' ? 'female' : '';

  const handleGuestCountChange = useGuestCountChange({ form, defaultGender });

  const guestConfigs: SpaGuestConfig[] = useMemo(() => {
    const guests = watchedGuests || [];
    return Array.from({ length: guestCount }, (_, i) => ({
      guestNumber: i + 1,
      values: {
        title: guests[i]?.title || '',
        firstName: guests[i]?.firstName || '',
        lastName: guests[i]?.lastName || '',
        gender: guests[i]?.gender || '',
      },
      existingGuests,
      selectedGuestId: guests[i]?.selectedGuestId,
    }));
  }, [watchedGuests, guestCount, existingGuests]);

  const genderOptions = useMemo(() => {
    const options = [
      { value: 'male', label: t('common.male') },
      { value: 'female', label: t('common.female') },
    ];
    if (showGenderNeutral) {
      options.push({ value: 'neutral', label: t('common.genderNeutral') });
    }
    return getGenderOptionsForRestriction(normaliseGenderCode(spaEntity?.genderSpecific), options)
      .genderOpts;
  }, [showGenderNeutral, spaEntity?.genderSpecific, t]);

  const handleGuestChange = (guestIndex: number, field: string, value: string) => {
    const currentGuests = form.getValues('guests') || [];
    const updatedGuests = [...currentGuests];
    if (!updatedGuests[guestIndex]) {
      updatedGuests[guestIndex] = { title: '', firstName: '', lastName: '', gender: '' };
    }
    updatedGuests[guestIndex] = { ...updatedGuests[guestIndex], [field]: value };
    form.setValue('guests', updatedGuests);
  };

  const handleExistingGuestSelect = useExistingGuestSelect({
    form,
    existingGuests,
  });

  const { handleFreeTextChange, preferenceCategories } = useNotesPreferences({ form });

  const handleTimeslotSelect = (timeslotValue: string) => {
    const daySlots = slotsByDate[selectedDateString];
    if (!daySlots) return;
    const slot = daySlots.find((s) => moment(s.startDateTime).format('HH:mm') === timeslotValue);
    if (slot) {
      form.setValue('arrivalTime', {
        startTime: moment(slot.startDateTime).format('HH:mm'),
        endTime: moment(slot.endDateTime).format('HH:mm'),
        startDateTime: moment(slot.startDateTime).format(),
        endDateTime: moment(slot.endDateTime).format(),
      });
    }
  };

  const hasExtras = !!(spaEntity?.extras?.length || spaEntity?.selectionGroups?.length);

  const { handleSubmit, handleNextStep, handleBack } = useSubmitTreatment({
    form,
    spaEntity: spaEntity || undefined,
    propertyId: property.id,
    language,
    id,
    onClose,
    hasExtras,
  });
  const { watch } = form;
  const formValues = watch();
  const isValid = spaEntityFormSchema.safeParse(formValues).success;
  const basePrice = spaEntity?.price?.amount || 0;
  const discountedPrice = spaEntity?.discount?.discountedPrice?.amount;
  const currencyCode = spaEntity?.price?.currencyCode || 'GBP';

  if (isLoadingEntity && !spaEntity) {
    return <Spinner isActive showDelayMessage />;
  }

  const isTelephoneOnly = spaEntity?.telephoneOnly || false;

  const guestLimitNote =
    (journeyParams?.spaAdults || 1) > maxGuests
      ? t('spa.maximumGuestsNotice', {
          spaEntity: t('spa.spaTreatment').toLowerCase(),
          maxGuests,
        })
      : undefined;

  return (
    <>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <SpaTreatmentFacilityBookingForm
        imageUrl={spaEntity?.image?.url}
        imageAlt={spaEntity?.name}
        onBack={handleBack}
        isLoading={calendarView === 'week' && isLoadingTimeslots}
        guestCount={guestCount}
        minGuests={minGuests}
        maxGuests={maxGuests}
        guestLimitNote={guestLimitNote}
        onGuestCountChange={handleGuestCountChange}
        activeDate={weekStartDate}
        selectedDate={selectedDateString}
        calendarView={calendarView}
        availability={calendarView === 'week' ? availability : undefined}
        onDateSelect={handleDateSelect}
        onWeekNavigate={setWeekStartDate}
        onCalendarViewChange={handleCalendarViewChange}
        timeslots={hasNoAvailabilityAllWeek ? [] : timeslotChips}
        selectedTimeslot={watchedArrivalTime?.startTime}
        onTimeslotSelect={handleTimeslotSelect}
        guests={hasNoAvailabilityAllWeek ? [] : guestConfigs}
        onExistingGuestSelect={handleExistingGuestSelect}
        onGuestChange={handleGuestChange}
        titleOptions={['Mr', 'Mrs', 'Ms', 'Miss', 'Dr']}
        genderOptions={genderOptions}
        preferenceCategories={preferenceCategories}
        onPreferenceOptionToggle={() => {}}
        onPreferenceOtherChange={() => {}}
        onPreferenceFreeTextChange={handleFreeTextChange}
        telephoneOnly={isTelephoneOnly}
        unavailable={hasNoAvailabilityToday || false}
        labels={{
          title: spaEntity?.name || '',
          subtitle: t('spa.booking.subtitle'),
          noteText: t('spa.package.arrivalTime'),
          ...bookingLabels,
          stickyFooter: {
            totalLabel: bookingLabels.stickyFooterTotalLabel,
            ctaLabel: hasExtras ? t('spa.package.nextStep') : t('spa.book'),
          },
          telephoneOnly: bookingLabels.telephoneOnly,
          unavailable: bookingLabels.unavailable,
        }}
        stickyFooter={
          !isTelephoneOnly && !hasNoAvailabilityToday
            ? {
                title: spaEntity?.name || '',
                totalAmount:
                  discountedPrice != null ? discountedPrice * guestCount : basePrice * guestCount,
                originalAmount: discountedPrice != null ? basePrice * guestCount : undefined,
                currencyCode,
                onCtaClick: hasExtras ? handleNextStep : handleSubmit,
                ctaDisabled: !isValid,
              }
            : undefined
        }
      />
    </>
  );
};
